import React, {useState, useCallback, useEffect} from 'react';
import {dropSampleTag} from 'utils/dropSampleTag.js';
import GraphicalEnvelope from '../envelope/GraphicalEnvelope.js';
import SamplerToolbar from './SamplerToolbar.js';
import AdvancedControls from './AdvancedControls.js';
import SamplerParameters from './SamplerParameters.js';
import ConditionedComponent from 'components/ConditionedComponent.js';
import { connect } from "react-redux";
import HorizontalPianoComponent from './HorizontalPianoComponent.js';
import {
    setSampleDragging,
    setSampleTagDragging,
    setPresetDragging,
} from "actions/index.js";
import TracksDropdown from 'components/dropdowns/TracksDropdown.js';
import CheckboxParameter from 'components/effects/CheckboxParameter.js';
import EffectParameterKnob from 'components/effects/EffectParameterKnob.js';
import PrettyWaveform from 'components/waveform/PrettyWaveform.js';
import InterpolationDropdown from './InterpolationDropdown.js';
import MixModeDropdown from './MixModeDropdown.js';
import MultiParameterSlider from './MultiParameterSlider.js';
import ADSRComponent from './ADSRComponent.js';
import Colors from 'components/Colors.js';

const colors = new Colors();

function SampledInstrumentComponent(props) {
    const [connectedTrack, setConnectedTrack] = useState(undefined);
    let instrument = props.track.instrument;

    const onSelectTrack = useCallback((track) => {
        instrument.connectToTrack(track);
        setConnectedTrack(track);
    });

    useEffect(() => {
        setConnectedTrack(instrument.connectedTrack);
    }, [instrument.connectedTrack]);

    useEffect(() => {
        return () => props.track.selectedSampler = props.instrument.defaultSampler;
    }, [props.track, props.instrument]);

    const [selectedSampler, setSelectedSampler] = useState(props.track.selectedSampler);
    const [showPianoRoll, setShowPianoRoll] = useState(Object.keys(instrument.samplers).length > 1);

    useEffect(() => {
        if (instrument.defaultSampler) {
            setSelectedSampler(instrument.defaultSampler);
            props.track.sample = instrument.defaultSampler.sample;
        }
    }, [instrument.defaultSampler]);

    const setFirstSampler = useCallback(() => {
        let sampler = instrument.allSamplers[0];
        if (sampler) {
            setSelectedSampler(sampler);
            props.track.sample = sampler.sample;
        } else {
            setSelectedSampler(undefined);
            setWaveform('');
        }
        props.track.selectedSampler = sampler;
    }, [setSelectedSampler, props.instrument, props.setWaveform]);

    const [waveform, setWaveform] = useState('');

    const deleteSample  = useCallback((sampler) => {
        props.instrument.deleteSampler(selectedSampler);
        setWaveform('');
        // setSelectedSampler(instrument.allSamplers[0]);
        setFirstSampler();
    }, [selectedSampler, setSelectedSampler, props.instrument, setWaveform]);

    const onClickSampler = useCallback((sampler) => {
        if (sampler === undefined) {
            return;
        }
        props.track.sample = sampler.sample;
        props.track.selectedSampler = sampler;
        if (selectedSampler === sampler) {
        } else {
            setSelectedSampler(sampler);
        }
    }, [selectedSampler, setSelectedSampler, props.track]);

    const playSample = useCallback(() => {
        instrument.playSampler(selectedSampler);
    }, [selectedSampler]);

    const onDrop = useCallback((e) => {
        console.log("ON DROP sampler");
        e.stopPropagation();
        let newSample = (sample, stream) => {
            props.instrument.newSample(undefined, sample, stream)
                .then((s) => {
                    let sampler = s.sampler;
                    props.track.sample = sampler.sample;
                    props.track.selectedSampler = sampler;
                    setSelectedSampler(sampler);
                });
        };
        if (props.sampleDragging) {
            newSample(props.sampleDragging);
            props.setSampleDragging(undefined);
        } else if (props.presetDragging) {
            props.track.auditionPreset(props.presetDragging);
            props.setPresetDragging(undefined);
        } else if (props.sampleTagDragging) {
            dropSampleTag(
                props.sampleTagDragging,
                props.track,
                props.setSampleTagDragging,
                newSample);
        }
    }, [
        props.track,
        setSelectedSampler,
        props.sampleDragging,
        props.sampleTagDragging,
        props.presetDragging,
        props.setPresetDragging]);

    let color = colors.getColorForTrack(props.track);
    return (
        <div
          onDrop={onDrop}
          onDragOver={(e) => e.preventDefault()}
          className="instrument sampled-instrument">
          <div>
           <SamplerToolbar
              sequencerEngine={props.sequencerEngine}
              track={props.track}
              sample={props.track.sample}
              instrument={instrument}
              selectedSampler={selectedSampler}
             deleteSample={deleteSample}
             presetName={props.presetName}
            />
            <div className="parameters-container">
              <div className="pane">
                <GraphicalEnvelope
                  name="Amp"
                  backgroundColor="#545d6e"
                  fullText={true}
                  width={270}
                  sequencerEngine={props.sequencerEngine}
                  fillColor={colors.brighten(color, 0.7)}
                  lineColor={color}
                  hasRelease={true}
                  hasLevel={false}
                  envelope={instrument}
                />
                <SamplerParameters
                  color={colors.brighten(color, 0.7)}
                  sampler={selectedSampler} instrument={instrument}/>
              </div>
              <AdvancedControls
                showPianoRoll={showPianoRoll}
                toggleShowPiano={() => setShowPianoRoll(!showPianoRoll)}
                setSelectedSampler={onClickSampler}
                sampler={selectedSampler}
                instrument={instrument}/>
           </div>
          </div>
            <ConditionedComponent condition={showPianoRoll}>
              <HorizontalPianoComponent
                onClickSampler={onClickSampler}
                selectedSampler={selectedSampler}
                track={props.track}
                sampleDragging={props.sampleDragging}
                instrument={instrument}/>
            </ConditionedComponent>
        </div>

    );
    /**
       <div className="advanced-controls">
       <InterpolationDropdown instrument={instrument}/>
       <MixModeDropdown instrument={instrument}/>
       <CheckboxParameter name="Monophonic" parameter={instrument.monophonicParameter}/>
       <CheckboxParameter name="Loop All" parameter={instrument.loopParameter}/>
       </div>
    */
}

function SelectedSamplerParameters(props) {
    if (props.sampler === undefined) {
        return (
            <div></div>
        );
    }

    return (
        <div className="sampler-parameters">
          <div className="parameters-row">
            <MultiParameterSlider
              sequencerEngine={props.sequencerEngine}
              parameters={{
                  "Trspe": {parameter: props.sampler.transposeParameter},
                  "Detun": {parameter: props.sampler.detuneParameter},
                  "Vol": {parameter: props.sampler.volumeParameter},
              }}/>
          </div>
          <div className="loop-parameter">
            <CheckboxParameter name="Loop" parameter={props.sampler.loopParameter}/>
            <div className="sample-name">
              {props.sampler.sample && props.sampler.sample.name ? truncateName(props.sampler.sample.name) : ''}
            </div>
            <div
              onClick={props.onDeleteSample}
              className="sample-delete-button">Delete</div>
          </div>
        </div>
    );
}

const mapStateToProps = (state, props) => {
    let sampler = props.track.selectedSampler;
    return {
        sampleDragging: state.selector.sampleDragging,
        sampleSlices: sampler ? state.tracks.sampleSlices[sampler.id] : undefined,
        presetDragging: state.presets.presetDragging,
        sampleTagDragging: state.selector.sampleTagDragging,
        sample: props.track.sample,
        presetName: props.track.presetName
    };
};
 const mapDispatchToProps = (dispatch, props) => {
    return {
        setSampleDragging: (sample) => dispatch(setSampleDragging(sample)),
        setPresetDragging: (preset) => dispatch(setPresetDragging(preset)),
        setSampleTagDragging: m => dispatch(setSampleTagDragging(m))
    };
};

function truncateName(str) {
    return str.length >= 16 ? str.substring(0, 16) : str;
}

export default connect(mapStateToProps, mapDispatchToProps)(SampledInstrumentComponent);



